import type { SxProps } from "@mui/material"
import { Box, Stack, Typography, useTheme } from "@mui/material"
import { useTranslation } from "react-i18next"

import { degreesToString } from "helpers/utils/common"

const T_PREFIX = "plots_descriptions.DEVICE_POSITION"

interface TowerRelativeAngleViewProps {
  src: string
  value: number
  viewKey: "first" | "second"
  rowHeight: number
  valueSx?: SxProps
  alt?: string
  // "tilt" (default) for device position, "lean" for the tower illustration
  labelVariant?: "tilt" | "lean"
}

export const TowerRelativeAngleView = ({
  src,
  value,
  viewKey,
  valueSx,
  alt,
  labelVariant = "tilt",
}: TowerRelativeAngleViewProps) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const isFirst = viewKey === "first"

  const titleKey =
    labelVariant === "lean"
      ? isFirst
        ? "FRONT_LEAN"
        : "RIGHT_LEAN"
      : isFirst
        ? "FRONT_TILT"
        : "RIGHT_TILT"
  const title = t(`${T_PREFIX}.${titleKey}`)
  const note = t(`${T_PREFIX}.${isFirst ? "TOWER_FROM_SIDE" : "TOWER_FROM_FRONT"}`)
  const posLabel = t(`${T_PREFIX}.${isFirst ? "FRONT" : "RIGHT"}`)
  const negLabel = t(`${T_PREFIX}.${isFirst ? "BACK" : "LEFT"}`)
  const color =
    theme.palette[isFirst ? "alpha" : "beta"]?.main ?? theme.palette.text.primary

  return (
    <Stack
      sx={{
        height: "100%",
        minHeight: 0,
        alignItems: "center",
        pt: 2,
        pb: 2,
        px: 3,
      }}
    >
      {/* Header: title, angle, note */}
      <Stack spacing={0.25} alignItems="center" sx={{ flexShrink: 0 }}>
        <Typography variant="overlineDense">{title}</Typography>
        <Typography sx={{ color, whiteSpace: "nowrap", ...valueSx }}>
          {degreesToString(value)}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", fontStyle: "italic", mt: 0.5 }}
        >
          {note}
        </Typography>
      </Stack>

      {/* Rotated tower image, bounded so it never overlaps the references */}
      <Box
        sx={{
          flex: 1,
          minHeight: 56,
          width: "100%",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Static 0° reference (plumb) line — marks the angle's starting point */}
        <Box
          sx={{
            position: "absolute",
            top: 12,
            bottom: 0,
            left: "50%",
            transform: "translateX(-50%)",
            borderLeft: "1px dashed",
            borderColor: "rgba(255, 255, 255, 0.2)",
          }}
        />
        <Box
          component="img"
          src={src}
          alt={alt ?? `${title} angle view`}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: `translate(-50%, -50%) rotate(${-value}deg)`,
            transformOrigin: "center center",
            height: "80%",
            width: "auto",
            maxWidth: "90%",
            objectFit: "contain",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: "10%",
            bottom: "10%",
            left: "50%",
            transform: `translateX(-50%) rotate(${-value}deg)`,
            transformOrigin: "center center",
            borderLeft: "1.5px solid",
            borderColor: color,
            opacity: 0.9,
          }}
        />
      </Box>

      {/* Tower-axis references */}
      <Box
        sx={{
          flexShrink: 0,
          width: "60%",
          mt: 1,
          alignSelf: "center",
          display: "grid",
          // minmax(0, 1fr) side columns can't grow to fit their label, so the
          // middle connector stays a fixed proportion — centered under the tower
          // and identical in size on every card, regardless of label text.
          gridTemplateColumns: "minmax(0, 1fr) 1.5fr minmax(0, 1fr)",
          alignItems: "center",
          columnGap: 1,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            whiteSpace: "nowrap",
            fontWeight: 700,
            justifySelf: "end",
          }}
        >
          {`${posLabel} (+)`}
        </Typography>
        <Box sx={{ width: "100%", display: "flex", alignItems: "center", gap: 0 }}>
          <svg width="9" height="10" style={{ flexShrink: 0, display: "block" }}>
            <polyline
              points="4,1 0,5 4,9"
              fill="none"
              stroke={theme.palette.neutral[600]}
              strokeWidth="1.25"
            />
            <line
              x1="0"
              y1="5"
              x2="9"
              y2="5"
              stroke={theme.palette.neutral[600]}
              strokeWidth="1.25"
            />
          </svg>
          <Box sx={{ flex: 1, height: "1px", bgcolor: theme.palette.neutral[600] }} />
          <svg width="9" height="10" style={{ flexShrink: 0, display: "block" }}>
            <line
              x1="0"
              y1="5"
              x2="9"
              y2="5"
              stroke={theme.palette.neutral[600]}
              strokeWidth="1.25"
            />
            <polyline
              points="5,1 9,5 5,9"
              fill="none"
              stroke={theme.palette.neutral[600]}
              strokeWidth="1.25"
            />
          </svg>
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            whiteSpace: "nowrap",
            fontWeight: 700,
            justifySelf: "start",
          }}
        >
          {`${negLabel} (-)`}
        </Typography>
      </Box>
    </Stack>
  )
}
